import requestV2 from '@/admin/api/requestV2';
import type { Pagination } from '@/admin/api/courts';

export type UserStatus = 'NOT_ACTIVE' | 'ACTIVE' | 'BLOCKED';

export interface AdminUser {
  id: number;
  email?: string;
  username?: string;
  imageLink?: string;
  lastLat?: number;
  lastLng?: number;
  available?: boolean;
  skillLevel?: string;
  experienceYears?: number;
  playFrequency?: string;
  matchesCount?: number;
  preferredRadiusKm?: number;
  referrals?: number[];
  admin?: boolean;
  status?: UserStatus;
  registrationDate?: string;
}

export interface UsersFilters {
  page?: number;
  pageSize?: number;
  query?: string;
  isAdmin?: boolean;
  status?: UserStatus;
  startDate?: string;
  endDate?: string;
  registrationDate?: string;
}

interface AdminUserListItemV2 {
  id: number;
  email?: string;
  username?: string;
  imageLink?: string;
  lastLat?: number;
  lastLng?: number;
  available?: boolean;
  skillLevel?: string;
  experienceYears?: number;
  playFrequency?: string;
  matchesCount?: number;
  preferredRadiusKm?: number;
  referrals?: number[];
  admin?: boolean;
  status?: UserStatus;
  registrationDate?: string;
}

function toAdminUser(item: any): AdminUser {
  const src = item as AdminUserListItemV2;
  return {
    id: src.id,
    email: src.email,
    username: src.username,
    imageLink: src.imageLink,
    lastLat: src.lastLat,
    lastLng: src.lastLng,
    available: src.available,
    skillLevel: src.skillLevel,
    experienceYears: src.experienceYears,
    playFrequency: src.playFrequency,
    matchesCount: src.matchesCount,
    preferredRadiusKm: src.preferredRadiusKm,
    referrals: Array.isArray(src.referrals) ? src.referrals : undefined,
    admin: (src as any).admin,
    status: src.status,
    registrationDate: src.registrationDate,
  };
}

export async function listUsers(filters: UsersFilters): Promise<Pagination<AdminUser>> {
  const zeroBasedPage = Math.max(0, (filters.page ?? 1) - 1);
  const pageSize = filters.pageSize ?? 10;
  const query: Record<string, any> = {
    page: zeroBasedPage,
    size: pageSize,
  };
  if (filters.query) query.query = filters.query;
  if (typeof filters.isAdmin === 'boolean') query.isAdmin = filters.isAdmin;
  if (filters.status) query.status = filters.status;
  if (filters.startDate) query.startDate = filters.startDate;
  if (filters.endDate) query.endDate = filters.endDate;
  if (filters.registrationDate) query.registrationDate = filters.registrationDate;

  const resp = await requestV2.get<any>('/users', { query });

  const rawItems: any[] = Array.isArray(resp)
    ? resp
    : Array.isArray((resp as any)?.items)
      ? (resp as any).items
      : Array.isArray((resp as any)?.content)
        ? (resp as any).content
        : [];

  const items = rawItems.map((it) => toAdminUser(it));
  const total = (resp && (resp.total ?? resp.count ?? resp.totalElements)) as number | undefined;
  const backendPage = (resp && (resp.page ?? resp.pageNumber)) as number | undefined;
  const outPage = ((backendPage ?? zeroBasedPage) + 1);
  const outPageSize = (resp && (resp.pageSize ?? resp.size)) || pageSize;
  const pageCount = (resp && (resp.pageCount ?? resp.countPage ?? resp.totalPages)) as number | undefined;

  return { items, total, page: outPage, pageSize: outPageSize, pageCount };
}

export async function getUserDetails(id: number): Promise<AdminUser> {
  const data = await requestV2.get<any>(`/users/${id}`);
  return toAdminUser(data);
}

export interface CreateUserRequest {
  email?: string;
  password?: string;
  username?: string;
  imageLink?: string;
  lastLat?: number;
  lastLng?: number;
  available?: boolean;
  skillLevel?: string;
  experienceYears?: number;
  playFrequency?: string;
  matchesCount?: number;
  preferredRadiusKm?: number;
  referrals?: number[];
  admin?: boolean;
}

export interface UpdateUserRequest extends CreateUserRequest {
  status?: UserStatus;
  registrationDate?: string;
  profile?: {
    rocketSportsExperience?: boolean;
    ntrpRating?: string;
    skillLevel?: string;
    city?: string;
    gender?: string;
    showGenderInProfile?: boolean;
    dateOfBirth?: string; // format YYYY-MM-DD
    padelClubMembership?: string;
    playingDuration?: string;
    playingFrequency?: string;
    aboutYourself?: string;
    industry?: string;
    instagram?: string;
    linkedin?: string;
  };
}

export async function createUser(payload: CreateUserRequest): Promise<AdminUser> {
  const data = await requestV2.post<any>('/users', { data: payload });
  return toAdminUser(data);
}

export async function updateUser(id: number, payload: UpdateUserRequest): Promise<AdminUser> {
  const data = await requestV2.patch<any>(`/users/${id}`, { data: payload });
  return toAdminUser(data);
}

export async function deleteUser(id: number): Promise<void> {
  await requestV2.delete<void>(`/users/${id}`);
}

export async function changeUserStatus(id: number, status: UserStatus, message?: string): Promise<AdminUser> {
  const body: { status: UserStatus; message?: string } = { status };
  if (message) body.message = message;
  const data = await requestV2.patch<any>(`/users/${id}/status`, { data: body });
  return toAdminUser(data);
}
